import { supabase } from './supabase';

export type S3GatewayBody = {
  action: string;
  payload?: Record<string, unknown>;
};

function unwrapError(data: unknown, fnError: Error | null) {
  if (fnError) throw new Error(fnError.message || 'Edge function invoke failed');
  if (data && typeof data === 'object' && 'error' in data) {
    const err = (data as { error?: string }).error;
    if (err) throw new Error(err);
  }
}

async function getFreshAccessToken(): Promise<string> {
  const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
  let session = sessionData.session;

  if (!session) {
    throw new Error(
      sessionErr?.message ||
        'Not authenticated: sign in again to use file storage',
    );
  }

  const expiresAtMs = (session.expires_at ?? 0) * 1000;
  const refreshWithinMs = 60_000;
  if (expiresAtMs > 0 && expiresAtMs - Date.now() < refreshWithinMs) {
    const { data: refreshed, error: refreshErr } = await supabase.auth.refreshSession();
    if (refreshErr) throw new Error(refreshErr.message);
    session = refreshed.session;
  }

  const accessToken = session?.access_token;
  if (!accessToken) {
    throw new Error('Not authenticated: sign in again to use file storage');
  }

  return accessToken;
}

export async function invokeS3Gateway<
  T extends Record<string, unknown> = Record<string, unknown>,
>(body: S3GatewayBody): Promise<T> {
  const accessToken = await getFreshAccessToken();

  const { data, error } = await supabase.functions.invoke<
    Record<string, unknown>
  >('s3-gateway', {
    body,
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  unwrapError(data, error ?? null);

  return (data ?? {}) as T;
}
